import React, { useState, useEffect } from 'react';
import { guestbookAPI, settingsAPI } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const AdminPage = () => {
  const [allEntries, setAllEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [coordinates, setCoordinates] = useState({ lat: '', lng: '' });
  const [settings, setSettings] = useState(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  useEffect(() => {
    fetchAllEntries();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const data = await settingsAPI.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Error al cargar configuración');
    }
  };

  const fetchAllEntries = async () => {
    try {
      setIsLoading(true);
      const data = await guestbookAPI.getAllEntries();
      setAllEntries(data);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Error al cargar entradas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCoordinates = async (entryId) => {
    if (!coordinates.lat || !coordinates.lng) {
      toast.error('Por favor, ingresa las coordenadas');
      return;
    }

    try {
      const coords = [parseFloat(coordinates.lat), parseFloat(coordinates.lng)];
      await guestbookAPI.updateCoordinates(entryId, coords);
      toast.success('Coordenadas actualizadas correctamente');
      setSelectedEntry(null);
      setCoordinates({ lat: '', lng: '' });
      fetchAllEntries();
    } catch (error) {
      console.error('Error updating coordinates:', error);
      toast.error('Error al actualizar las coordenadas');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta entrada?')) {
      return;
    }

    try {
      await guestbookAPI.deleteEntry(entryId);
      toast.success('Entrada eliminada');
      fetchAllEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Error al eliminar la entrada');
    }
  };

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveSettings = async () => {
    try {
      setIsSavingSettings(true);
      await settingsAPI.updateSettings(settings);
      toast.success('Textos actualizados correctamente');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleResetSettings = async () => {
    if (!window.confirm('¿Estás seguro de restaurar los textos por defecto?')) {
      return;
    }

    try {
      const defaults = await settingsAPI.resetSettings();
      setSettings(defaults);
      toast.success('Textos restaurados a valores por defecto');
    } catch (error) {
      console.error('Error resetting settings:', error);
      toast.error('Error al restaurar configuración');
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1 className="admin-title">Panel de Administración - Libro de Visitas</h1>
        <a href="/" className="admin-back-link">← Volver al sitio</a>
      </header>

      <main className="admin-content">
        <Tabs defaultValue="entries" className="admin-tabs">
          <TabsList className="admin-tabs-list">
            <TabsTrigger value="entries" className="admin-tab">
              📋 Todas las Entradas ({allEntries.length})
            </TabsTrigger>
            <TabsTrigger value="texts" className="admin-tab">
              ✏️ Editar Textos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="entries" className="admin-tab-content">
            {isLoading ? (
              <div className="admin-loading">Cargando entradas...</div>
            ) : allEntries.length === 0 ? (
              <div className="admin-empty">
                <p>📭 No hay entradas todavía</p>
                <p>Las entradas aparecerán aquí cuando los usuarios dejen mensajes.</p>
              </div>
            ) : (
              <div className="admin-grid">
                {allEntries.map((entry) => (
                  <Card key={entry.id} className="admin-entry-card">
                    <div className="admin-entry-header">
                      <h3>{entry.name}</h3>
                      <span className="admin-entry-location">📍 {entry.location}</span>
                      {entry.coordinates && (
                        <span className="admin-entry-coords">
                          🗺️ ({entry.coordinates[0].toFixed(4)}, {entry.coordinates[1].toFixed(4)})
                        </span>
                      )}
                    </div>
                
                <p className="admin-entry-message">{entry.message}</p>
                
                {entry.image && (
                  <div className="admin-entry-image">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${entry.image}`} 
                      alt="Imagen del usuario" 
                    />
                  </div>
                )}
                
                <div className="admin-entry-date">
                  {new Date(entry.timestamp).toLocaleString('es-ES')}
                </div>

                {selectedEntry === entry.id ? (
                  <div className="admin-entry-actions-expanded">
                    <div className="admin-coordinates">
                      <label>Asignar Coordenadas (opcional - para mostrar en el mapa)</label>
                      <div className="admin-coord-inputs">
                        <Input
                          type="number"
                          step="any"
                          placeholder="Latitud (ej: 40.4168)"
                          value={coordinates.lat}
                          onChange={(e) => setCoordinates({ ...coordinates, lat: e.target.value })}
                        />
                        <Input
                          type="number"
                          step="any"
                          placeholder="Longitud (ej: -3.7038)"
                          value={coordinates.lng}
                          onChange={(e) => setCoordinates({ ...coordinates, lng: e.target.value })}
                        />
                      </div>
                      <a 
                        href={`https://www.google.com/maps/search/${encodeURIComponent(entry.location)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-maps-link"
                      >
                        🗺️ Buscar en Google Maps
                      </a>
                    </div>
                    
                    <div className="admin-action-buttons">
                      <Button 
                        onClick={() => handleUpdateCoordinates(entry.id)}
                        className="admin-btn-approve"
                      >
                        ✓ Guardar Coordenadas
                      </Button>
                      <Button 
                        onClick={() => setSelectedEntry(null)}
                        variant="outline"
                        className="admin-btn-cancel"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="admin-entry-actions">
                    <Button 
                      onClick={() => setSelectedEntry(entry.id)}
                      className="admin-btn-review"
                    >
                      📍 {entry.coordinates ? 'Editar' : 'Añadir'} Coordenadas
                    </Button>
                    <Button 
                      onClick={() => handleDelete(entry.id)}
                      variant="destructive"
                      className="admin-btn-reject"
                    >
                      🗑️ Eliminar
                    </Button>
                  </div>
                )}
              </Card>
            ))}
          </div>
            )}
          </TabsContent>

          <TabsContent value="texts" className="admin-tab-content">
            {settings && (
              <div className="settings-editor">
                <div className="settings-header">
                  <h2>Editar Textos de la Landing Page</h2>
                  <div className="settings-actions">
                    <Button 
                      onClick={handleSaveSettings}
                      disabled={isSavingSettings}
                      className="admin-btn-save"
                    >
                      {isSavingSettings ? '⏳ Guardando...' : '💾 Guardar Cambios'}
                    </Button>
                    <Button 
                      onClick={handleResetSettings}
                      variant="outline"
                      className="admin-btn-reset"
                    >
                      🔄 Restaurar Por Defecto
                    </Button>
                  </div>
                </div>

                <div className="settings-grid">
                  <Card className="settings-section">
                    <h3>Sección Hero</h3>
                    <div className="form-group">
                      <Label>Título Principal</Label>
                      <Input
                        value={settings.hero_title}
                        onChange={(e) => handleSettingsChange('hero_title', e.target.value)}
                        placeholder="¡Celebra con nosotros!"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Texto Descriptivo</Label>
                      <Textarea
                        value={settings.hero_text}
                        onChange={(e) => handleSettingsChange('hero_text', e.target.value)}
                        placeholder="Únete a nuestra celebración..."
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <Label>Texto Botón Principal</Label>
                      <Input
                        value={settings.cta_button_text}
                        onChange={(e) => handleSettingsChange('cta_button_text', e.target.value)}
                        placeholder="✍️ Dejar un mensaje"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Texto Botón Alternativo</Label>
                      <Input
                        value={settings.cta_button_text_alt}
                        onChange={(e) => handleSettingsChange('cta_button_text_alt', e.target.value)}
                        placeholder="🗺️ Ver el mapa"
                      />
                    </div>
                  </Card>

                  <Card className="settings-section">
                    <h3>Sección del Mapa</h3>
                    <div className="form-group">
                      <Label>Título del Mapa</Label>
                      <Input
                        value={settings.map_title}
                        onChange={(e) => handleSettingsChange('map_title', e.target.value)}
                        placeholder="🌍 Mensajes desde el mundo"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Descripción</Label>
                      <Input
                        value={settings.map_description}
                        onChange={(e) => handleSettingsChange('map_description', e.target.value)}
                        placeholder="Haz clic en los marcadores..."
                      />
                    </div>
                  </Card>

                  <Card className="settings-section">
                    <h3>Sección del Formulario</h3>
                    <div className="form-group">
                      <Label>Título del Formulario</Label>
                      <Input
                        value={settings.form_title}
                        onChange={(e) => handleSettingsChange('form_title', e.target.value)}
                        placeholder="📝 Firma nuestro libro de visitas"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Subtítulo</Label>
                      <Input
                        value={settings.form_subtitle}
                        onChange={(e) => handleSettingsChange('form_subtitle', e.target.value)}
                        placeholder="Déjanos tu mensaje de celebración"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Nota Informativa</Label>
                      <Input
                        value={settings.form_note}
                        onChange={(e) => handleSettingsChange('form_note', e.target.value)}
                        placeholder="* Tu mensaje será revisado..."
                      />
                    </div>
                  </Card>

                  <Card className="settings-section">
                    <h3>Sección de Mensajes</h3>
                    <div className="form-group">
                      <Label>Título</Label>
                      <Input
                        value={settings.messages_title}
                        onChange={(e) => handleSettingsChange('messages_title', e.target.value)}
                        placeholder="💬 Últimos mensajes"
                      />
                    </div>
                  </Card>

                  <Card className="settings-section">
                    <h3>Estadísticas</h3>
                    <div className="form-group">
                      <Label>Etiqueta "Mensajes"</Label>
                      <Input
                        value={settings.stat_messages_label}
                        onChange={(e) => handleSettingsChange('stat_messages_label', e.target.value)}
                        placeholder="Mensajes"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Etiqueta "Ubicaciones"</Label>
                      <Input
                        value={settings.stat_locations_label}
                        onChange={(e) => handleSettingsChange('stat_locations_label', e.target.value)}
                        placeholder="Ubicaciones"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Etiqueta "Años"</Label>
                      <Input
                        value={settings.stat_years_label}
                        onChange={(e) => handleSettingsChange('stat_years_label', e.target.value)}
                        placeholder="Años"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Número de Años</Label>
                      <Input
                        type="number"
                        value={settings.stat_years_number}
                        onChange={(e) => handleSettingsChange('stat_years_number', parseInt(e.target.value))}
                        placeholder="20"
                      />
                    </div>
                  </Card>

                  <Card className="settings-section">
                    <h3>Footer</h3>
                    <div className="form-group">
                      <Label>Texto del Footer</Label>
                      <Input
                        value={settings.footer_text}
                        onChange={(e) => handleSettingsChange('footer_text', e.target.value)}
                        placeholder="© 2026 Enfusión - 20 años de pasión digital"
                      />
                    </div>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPage;